import React from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import TzDifferenceIndicator from './TzDifferenceIndicator';

dayjs.extend(timezone);
dayjs.extend(utc);
/**
 * Displays a formatted date and time range for a calendar event.
 *
 * @component
 * @param {Object} props
 * @param {dayjs.Dayjs} props.start - The start date/time as a dayjs object.
 * @param {dayjs.Dayjs} props.end - The end date/time as a dayjs object.
 * @param {'timespan'|'datespan'} props.whenType - Type of event span: 'timespan' for time ranges,
 *        'datespan' for all-day/multi-day.
 * @param {string} props.timeFormat - Format string for displaying times (e.g., 'h:mm A').
 * @param {string} props.shortTz - Short timezone label to display (e.g., 'PST').
 * @param {boolean} props.sameDay - Whether the event starts and ends on the same day.
 * @returns {JSX.Element} Formatted date/time display for the event.
 */
export const EventDateTimeDisplay = ({
  start,
  end,
  whenType,
  timeFormat,
  displayTz,
  sameDay,
  originalOffset,
}) => {
  const startDateString = start.format('dddd, MMMM D');
  // Date span includes next day at 00:00.
  const endDateString = whenType === 'datespan' ? end.subtract(1, 'day').format('dddd, MMMM D') :
    end.format('dddd, MMMM D');
  const startTime = start.format(timeFormat);
  const endTime = end.format(timeFormat);
  const shortTz = start.format('z');
  const isDifferentOffset = originalOffset !== start.utcOffset();

  if (!sameDay && whenType === 'timespan') {
    return (
      <div className="event-card-datetime event-card-datetime--timespan-multiday">
        <div>
          {startDateString} {startTime} {shortTz}-
          <TzDifferenceIndicator
            show={isDifferentOffset}
            time={start}
            originalOffset={originalOffset}
            timeFormat={timeFormat}
            displayTz={displayTz}
          />
        </div>
        <div>
          {endDateString} {endTime} {shortTz}
          <TzDifferenceIndicator
            show={isDifferentOffset}
            time={end}
            originalOffset={originalOffset}
            timeFormat={timeFormat}
            displayTz={displayTz}
          />
        </div>
      </div>
    );
  }

  if (!sameDay && whenType === 'datespan') {
    return (
      <div className="event-card-datetime event-card-datetime--datespan-multiday">
        {startDateString} -<br />
        {endDateString}<br />
        All Day
      </div>
    );
  }

  if (whenType === 'timespan') {
    return (
      <div className="event-card-datetime event-card-datetime--timespan">
        <div>{startDateString}</div>
        <div>
          Start: {startTime} {shortTz}
          <TzDifferenceIndicator
            show={isDifferentOffset}
            time={start}
            originalOffset={originalOffset}
            timeFormat={timeFormat}
            displayTz={displayTz}
          />
        </div>
        <div>
          End: {endTime} {shortTz}
          <TzDifferenceIndicator
            show={isDifferentOffset}
            time={end}
            originalOffset={originalOffset}
            timeFormat={timeFormat}
            displayTz={displayTz}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="event-card-datetime event-card-datetime--datespan">
      {startDateString}<br />
      All Day
    </div>
  );
};
